import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';

const AuthModal = ({ isOpen, onClose, onAuthSuccess }) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1: Phone, 2: OTP, 3: Profile (if needed)
  const [isNewUser, setIsNewUser] = useState(false);

  // Form data
  const [phone, setPhone] = useState('+91'); // Default India country code
  const [otp, setOtp] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    addAddress: false,
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  });

  // UI states
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [countdown, setCountdown] = useState(0);
  const [otpSent, setOtpSent] = useState(false);

  const modalRef = useRef(null);
  const navigate = useNavigate();

  const {
    sendPhoneOTP,
    verifyPhoneOTP,
    error,
    loading,
    otpSent: contextOtpSent,
  } = useAuth();

  // Set otpSent based on context
  useEffect(() => {
    if (contextOtpSent) {
      setOtpSent(true);
      setCurrentStep(2);
    }
  }, [contextOtpSent]);

  // Countdown timer for OTP resend
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Reset state when modal is opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setOtp('');
      setOtpSent(false);
      setFormError('');
      setFieldErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });

    // Clear field-specific error when user types
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: '',
      });
    }
  };

  const validatePhoneStep = () => {
    if (!phone) {
      setFormError('Please enter your phone number');
      return false;
    }

    // Basic phone validation
    if (!/^\+?[0-9]{10,15}$/.test(phone)) {
      setFormError(
        'Please enter a valid Indian phone number (e.g., +91 9876543210)'
      );
      return false;
    }

    return true;
  };

  const validateOTPStep = () => {
    if (!otp) {
      setFormError('Please enter the OTP sent to your phone');
      return false;
    }

    // Basic OTP validation
    if (!/^[0-9]{6}$/.test(otp)) {
      setFormError('OTP must be 6 digits');
      return false;
    }

    return true;
  };

  const validateProfileStep = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    // Validate address fields if address is being added
    if (formData.addAddress) {
      if (!formData.street.trim()) {
        errors.street = 'Street address is required';
      }
      if (!formData.city.trim()) {
        errors.city = 'City is required';
      }
      if (!formData.postal_code.trim()) {
        errors.postal_code = 'Postal code is required';
      }
      if (!formData.country.trim()) {
        errors.country = 'Country is required';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendOTP = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFormError('');

    if (!validatePhoneStep()) {
      return;
    }

    try {
      await sendPhoneOTP(phone);
      setOtpSent(true);
      setCountdown(30); // 30 seconds countdown for resend
      setCurrentStep(2);
    } catch (err) {
      setFormError(err.message || 'Failed to send OTP');
    }
  };

  const handleVerifyOTP = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFormError('');

    if (!validateOTPStep()) {
      return;
    }

    try {
      // First, try to verify OTP without user data to check if user exists
      try {
        console.log('Verifying OTP...');
        // If this succeeds, user exists and we proceed
        const user = await verifyPhoneOTP(otp);
        console.log('OTP verification successful!', user);
        console.log(
          'Token stored in localStorage:',
          localStorage.getItem('token')
        );

        // Close the modal
        onClose();

        // Call onAuthSuccess if provided, otherwise redirect to profile
        if (onAuthSuccess) {
          console.log('Calling onAuthSuccess callback...');
          onAuthSuccess();
        } else {
          navigate('/profile'); // Default behavior: redirect to profile page
        }
      } catch (err) {
        // If error contains "User not found", we need to collect user data
        if (err.message && err.message.includes('User not found')) {
          setIsNewUser(true);
          setCurrentStep(3); // Move to profile completion step
        } else {
          // Other verification error
          throw err;
        }
      }
    } catch (err) {
      setFormError(err.message || 'OTP verification failed');
    }
  };

  const handleCompleteRegistration = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFormError('');

    if (!validateProfileStep()) {
      setFormError('Please fix the errors in the form');
      return;
    }

    const userData = {
      name: formData.name,
      email: formData.email || null,
    };

    // If address is being added
    if (formData.addAddress) {
      userData.address = {
        line1: formData.street || '',
        line2: '',
        city: formData.city || '',
        state: formData.state || '',
        postal_code: formData.postal_code || '',
        country: formData.country || '',
        is_default: true,
        phone: phone, // Use phone as is
      };
    }

    try {
      await verifyPhoneOTP(otp, userData);
      onClose(); // Close the modal

      // Call onAuthSuccess if provided, otherwise redirect to profile
      if (onAuthSuccess) {
        onAuthSuccess();
      } else {
        navigate('/profile'); // Default behavior: redirect to profile page
      }
    } catch (err) {
      setFormError(err.message || 'Registration failed');
    }
  };

  // Render phone input form (Step 1)
  const renderPhoneForm = () => (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label
          htmlFor="phone"
          className="block text-sm font-medium text-text-dark"
        >
          Phone Number <span className="text-red-500">*</span>
        </label>
        <Input
          id="phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+91 9876543210"
          required
          className="w-full"
          onKeyPress={(e) => {
            // Submit on Enter key
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSendOTP();
            }
          }}
        />
        <p className="text-xs text-gray-500 mt-1">
          Enter your 10-digit phone number with India country code (+91)
        </p>
      </div>

      <Button
        type="button"
        onClick={handleSendOTP}
        className="w-full bg-primary hover:bg-primary/90 text-white"
        disabled={loading || !phone}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Sending OTP
          </div>
        ) : (
          'Send OTP'
        )}
      </Button>
    </div>
  );

  // Render OTP input form (Step 2)
  const renderOTPForm = () => (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex justify-between items-center">
          <label
            htmlFor="otp"
            className="block text-sm font-medium text-text-dark"
          >
            Enter OTP <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            className="text-xs text-primary hover:text-primary/80 flex items-center"
            onClick={() => setCurrentStep(1)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-1 h-3 w-3" />
            Change phone
          </button>
        </div>
        <Input
          id="otp"
          type="text"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          placeholder="Enter 6-digit OTP"
          required
          className="w-full text-center tracking-widest text-lg"
          maxLength={6}
          onKeyPress={(e) => {
            // Submit on Enter key
            if (e.key === 'Enter') {
              e.preventDefault();
              handleVerifyOTP();
            }
          }}
        />
        <p className="text-xs text-gray-500 mt-1">
          OTP sent to {phone}. {countdown > 0 ? `Resend in ${countdown}s` : ''}
        </p>
      </div>

      <Button
        type="button"
        onClick={handleVerifyOTP}
        className="w-full bg-primary hover:bg-primary/90 text-white"
        disabled={loading || !otp}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Verifying
          </div>
        ) : (
          'Verify OTP'
        )}
      </Button>

      {countdown === 0 && (
        <button
          type="button"
          onClick={handleSendOTP}
          disabled={loading}
          className="w-full mt-2 text-primary hover:text-primary/80 text-sm font-medium"
        >
          Resend OTP
        </button>
      )}
    </div>
  );

  // Render profile form (Step 3)
  const renderProfileForm = () => (
    <div className="space-y-4">
      {/* Full Name */}
      <div className="space-y-1.5">
        <label
          htmlFor="name"
          className="block text-sm font-medium text-text-dark"
        >
          Full Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="John Doe"
          className={fieldErrors.name ? 'border-red-400' : ''}
        />
        {fieldErrors.name && (
          <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>
        )}
      </div>

      {/* Email (Optional) */}
      <div className="space-y-1.5">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-text-dark"
        >
          Email Address <span className="text-gray-400">(Optional)</span>
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="your@email.com"
          className={fieldErrors.email ? 'border-red-400' : ''}
        />
        {fieldErrors.email && (
          <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
        )}
      </div>

      {/* Address Checkbox */}
      <div className="pt-2">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            name="addAddress"
            checked={formData.addAddress}
            onChange={handleChange}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm text-text-dark">
            Add address details (optional)
          </span>
        </label>
      </div>

      {/* Conditional Address Fields */}
      {formData.addAddress && (
        <div className="mt-4 p-4 bg-gray-50 rounded-md space-y-4">
          {/* Street */}
          <div className="space-y-1.5">
            <label
              htmlFor="street"
              className="block text-sm font-medium text-text-dark"
            >
              Street Address
            </label>
            <Input
              id="street"
              name="street"
              value={formData.street}
              onChange={handleChange}
              placeholder="123 Main Street"
              className={fieldErrors.street ? 'border-red-400' : ''}
            />
            {fieldErrors.street && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.street}</p>
            )}
          </div>

          {/* City */}
          <div className="space-y-1.5">
            <label
              htmlFor="city"
              className="block text-sm font-medium text-text-dark"
            >
              City
            </label>
            <Input
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Mumbai"
              className={fieldErrors.city ? 'border-red-400' : ''}
            />
            {fieldErrors.city && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.city}</p>
            )}
          </div>

          {/* State */}
          <div className="space-y-1.5">
            <label
              htmlFor="state"
              className="block text-sm font-medium text-text-dark"
            >
              State/Province
            </label>
            <Input
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="Maharashtra"
            />
          </div>

          {/* Postal Code */}
          <div className="space-y-1.5">
            <label
              htmlFor="postal_code"
              className="block text-sm font-medium text-text-dark"
            >
              Postal Code
            </label>
            <Input
              id="postal_code"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleChange}
              placeholder="400001"
              className={fieldErrors.postal_code ? 'border-red-400' : ''}
            />
            {fieldErrors.postal_code && (
              <p className="mt-1 text-sm text-red-600">
                {fieldErrors.postal_code}
              </p>
            )}
          </div>

          {/* Country */}
          <div className="space-y-1.5">
            <label
              htmlFor="country"
              className="block text-sm font-medium text-text-dark"
            >
              Country
            </label>
            <Input
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder="India"
              className={fieldErrors.country ? 'border-red-400' : ''}
            />
            {fieldErrors.country && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.country}</p>
            )}
          </div>
        </div>
      )}

      <Button
        type="button"
        onClick={handleCompleteRegistration}
        className="w-full mt-2 bg-primary hover:bg-primary/90 text-white"
        disabled={loading}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Completing Registration
          </div>
        ) : (
          'Complete Registration'
        )}
      </Button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-md mx-4 bg-white rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
        </button>

        <div className="p-6">
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-bold font-heading text-primary">
              {currentStep === 3 ? 'Complete Your Profile' : 'Account Access'}
            </h1>
            <p className="text-gray-600 mt-2">
              {currentStep === 1
                ? 'Enter your phone number to continue with your subscription'
                : currentStep === 2
                ? 'Verify your phone number'
                : 'Please provide your details to complete your subscription'}
            </p>
          </div>

          {(error || formError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-start">
              <svg
                className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error || formError}</span>
            </div>
          )}

          <div onClick={(e) => e.stopPropagation()}>
            {currentStep === 1 && renderPhoneForm()}
            {currentStep === 2 && renderOTPForm()}
            {currentStep === 3 && renderProfileForm()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
